/* eslint-disable prettier/prettier */
/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDAvatar from "components/MDAvatar";
import MDTypography from "components/MDTypography";
// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";
import Divider from "@mui/material/Divider";
import Badge from "@mui/material/Badge";
import MailIcon from "@mui/icons-material/Mail";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
  navbarMobileMenuTop,
  notificacionEfect,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

import {
  getTotalNotificacionesNoLeidas,
  getMostrarNotificaciones,
  MarcarNotificacionComoLeida,
} from "store/solicitudes/solicitudesActions";

import menuItem from "examples/Items/NotificationItem/styles";
import team2 from "assets/images/team-4.jpg";
import team5 from "assets/images/team-5.jpg";
import useInterval from "./validaNotificaciones";
import Loading from "components/loading";
import Notificaciones from "./Notificaciones";

import { store, persistor } from "store/store";
import { LogOutt } from "store/login/loginSlice";

function DashboardNavbar({ absolute, light, isMini }) {
  const { countNotificacionesNoLeidas, isloadingMostrarNotificaciones } = useSelector(
    (state) => state.solicitud
  );
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [openMenuNoti, setOpenMenuNoti] = useState(false);
  const [openMenuAyuda, setOpenMenuAyuda] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const dispatchRedux = useDispatch();
  const navigate = useNavigate();
  const usuario = store.getState().login.usuario;
  const token = store.getState().login.token;

  //Ejecutar actualizacion de notificaciones cada minuto
  useInterval(() => {
    getTotalNotificacionesNoLeidas(dispatchRedux);
  }, 1 * 60 * 1000);

  useEffect(() => {
    getTotalNotificacionesNoLeidas(dispatchRedux);
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const handleOpenMenuAyuda = (event) => setOpenMenuAyuda(event.currentTarget);
  const handleCloseMenuAyuda = () => setOpenMenuAyuda(false);

  const handleOpenMenuNoti = (event) => {
    setOpenMenuNoti(event.currentTarget);
    getMostrarNotificaciones(dispatchRedux);
  };
  const handleCloseMenuNoti = () => setOpenMenuNoti(false);

  const renderNotificaciones = () => {
    return (
      <Menu
        anchorEl={openMenuNoti}
        anchorReference={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={Boolean(openMenuNoti)}
        onClose={handleCloseMenuNoti}
        sx={{ mt: 2 }}
        PaperProps={{
          style: {
            maxHeight: "50rem",
            width: "25rem",
          },
        }}
      >
        {isloadingMostrarNotificaciones ? (
          <Loading title="Procesando por favor espere..." />
        ) : (
          <Notificaciones
            VerNotificacion={(id, vista) => {
              navigate("/detalles", {
                state: {
                  id: id,
                  vista: vista,
                },
              });
              handleCloseMenuNoti();
              MarcarNotificacionComoLeida(dispatchRedux, id);
            }}
          />
        )}
      </Menu>
    );
  };
  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        icon={<Icon>account_circle</Icon>}
        title="Pefil"
        onClick={() => {
          navigate("/perfil", {
            state: {
              ruta: "/" + route,
            },
          });
        }}
      />
      <NotificationItem
        icon={<Icon>login</Icon>}
        title="Salir"
        onClick={() => {
          dispatchRedux(LogOutt(token));
          persistor.purge();
          navigate("/");
        }}
      />
    </Menu>
  );

  const renderMenuAyuda = () => (
    <Menu
      anchorEl={openMenuAyuda}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenuAyuda)}
      onClose={handleCloseMenuAyuda}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        icon={<Icon>auto_stories</Icon>}
        title="Manual de usuario"
        onClick={() => {
          if (usuario) {
            if (usuario.rolUsuario == "Administración") {
              const nombreArchivo =
                "MANUAL DE USUARIO - ADMINISTRADOR ESCUCHAR SIN FRONTERAS v2.pdf";
              window.open(`/ayuda/${encodeURIComponent(nombreArchivo)}`, "_blank");
            } else {
              const nombreArchivo =
                "MANUAL DE USUARIO - ESPECIALISTA ESCUCHAR SIN FRONTERAS v2.pdf";
              window.open(`/ayuda/${encodeURIComponent(nombreArchivo)}`, "_blank");
            }
          }
        }}
      />
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });
  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="primary" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <MDBox>
            <IconButton
              size="small"
              color="inherit"
              sx={navbarMobileMenuTop}
              onClick={handleMiniSidenav}
            >
              <Icon sx={iconsStyle} fontSize="medium">
                {miniSidenav ? "menu_open" : "menu"}
              </Icon>
            </IconButton>
          </MDBox>
          <Breadcrumbs icon="" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"}>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>

              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenuAyuda}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  help
                </Icon>
              </IconButton>
              {renderMenuAyuda()}

              <Badge
                badgeContent={countNotificacionesNoLeidas}
                color="error"
                onClick={handleOpenMenuNoti}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                sx={{ ...notificacionEfect, marginLeft: 1 }}
              >
                <Icon sx={iconsStyle}>notifications</Icon>
              </Badge>

              {renderNotificaciones()}
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <MDAvatar size="xs" shadow="sm" bgColor="info">
                  {`${usuario?.nombre?.charAt(0) || ""}`}
                </MDAvatar>
                <MDTypography
                  variant="h6"
                  color="white"
                  style={{ opacity: 0.7, fontSize: "0.7rem" }}
                >
                  {`${usuario?.nombre || ""} ${usuario?.apellioPaterno || ""}`}
                </MDTypography>
              </IconButton>
              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
